// Pure scheduling helpers for the React planning drag/drop and inbox flow.
// Kept UI-agnostic so drag, touch and keyboard interactions use the same rules.
import {
  addIsoDays,
  daysBetweenInclusive,
  mondayForIsoDate,
} from "../../planning/planning-core.js";

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const DAY_MS = 86_400_000;

export interface PlanningRangeItem {
  startDate?: string | null;
  dueDate?: string | null;
}

export interface PlanningDateRange {
  startDate: string;
  dueDate: string;
}

function requireIsoDate(value: string, field: string): string {
  if (typeof value !== "string" || !ISO_DATE.test(value)) {
    throw new Error(`${field} moet YYYY-MM-DD zijn.`);
  }
  const date = new Date(`${value}T00:00:00.000Z`);
  if (
    Number.isNaN(date.getTime()) || date.toISOString().slice(0, 10) !== value
  ) {
    throw new Error(`${field} moet YYYY-MM-DD zijn.`);
  }
  return value;
}

function normalizedRange(item: PlanningRangeItem): PlanningDateRange | null {
  const startDate = item?.startDate ?? item?.dueDate ?? null;
  const dueDate = item?.dueDate ?? item?.startDate ?? null;
  if (!startDate || !dueDate) return null;
  return { startDate, dueDate };
}

export function signedIsoDayDifference(
  fromDate: string,
  toDate: string,
): number {
  const from = new Date(
    `${requireIsoDate(fromDate, "fromDate")}T00:00:00.000Z`,
  );
  const to = new Date(`${requireIsoDate(toDate, "toDate")}T00:00:00.000Z`);
  return Math.round((to.getTime() - from.getTime()) / DAY_MS);
}

export function placementDateInWindow(
  item: PlanningRangeItem,
  windowStart: string,
  windowEnd: string,
): string | null {
  requireIsoDate(windowStart, "windowStart");
  requireIsoDate(windowEnd, "windowEnd");
  if (windowEnd < windowStart) {
    throw new Error("windowEnd ligt voor windowStart.");
  }

  const range = normalizedRange(item);
  if (!range || range.dueDate < windowStart || range.startDate > windowEnd) {
    return null;
  }
  if (range.startDate < windowStart) return windowStart;
  return range.startDate;
}

export function placementWeekInHorizon(
  item: PlanningRangeItem,
  horizonStart: string,
  horizonEnd: string,
): string | null {
  requireIsoDate(horizonStart, "horizonStart");
  requireIsoDate(horizonEnd, "horizonEnd");
  if (horizonEnd < horizonStart) {
    throw new Error("horizonEnd ligt voor horizonStart.");
  }

  const range = normalizedRange(item);
  if (!range || range.dueDate < horizonStart || range.startDate > horizonEnd) {
    return null;
  }
  if (range.startDate < horizonStart) return mondayForIsoDate(horizonStart);
  return mondayForIsoDate(range.startDate);
}

export function placeUnscheduledAtDate(targetDate: string): PlanningDateRange {
  const date = requireIsoDate(targetDate, "targetDate");
  return { startDate: date, dueDate: date };
}

export function shiftRangeToDay(
  item: PlanningRangeItem,
  targetDate: string,
): PlanningDateRange {
  requireIsoDate(targetDate, "targetDate");
  if (!item?.startDate || !item?.dueDate) {
    throw new Error("Planningitem heeft een start- en einddatum nodig.");
  }
  const durationDays = daysBetweenInclusive(item.startDate, item.dueDate);
  return {
    startDate: targetDate,
    dueDate: addIsoDays(targetDate, durationDays - 1),
  };
}

export function shiftRangeToWeek(
  item: PlanningRangeItem,
  targetWeekStart: string,
): PlanningDateRange {
  requireIsoDate(targetWeekStart, "targetWeekStart");
  if (!item?.startDate || !item?.dueDate) {
    throw new Error("Planningitem heeft een start- en einddatum nodig.");
  }
  const currentWeekStart = mondayForIsoDate(item.startDate);
  const normalizedTarget = mondayForIsoDate(targetWeekStart);
  const deltaDays = signedIsoDayDifference(currentWeekStart, normalizedTarget);
  if (deltaDays % 7 !== 0) {
    throw new Error("Weekverschuiving moet uit hele weken bestaan.");
  }
  return {
    startDate: addIsoDays(item.startDate, deltaDays),
    dueDate: addIsoDays(item.dueDate, deltaDays),
  };
}
